package dev.lingvo.site.pages.app.settings.agents

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.AgentConnectionDto
import dev.lingvo.shared.ProjectSummaryDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.settings.loadWorkspaceProtectedResource
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext

/**
 * Connecting an agent, guided.
 *
 * **The defaults are the security model, so the page has to make them visible rather than merely
 * apply them.** It opens read-only, proposing rather than writing, with all three budgets at zero;
 * every widening is a thing somebody chose, and the page says what each one lets the agent do to
 * this workspace before they choose it.
 *
 * The one guarantee stated in plain words is the one that makes the rest acceptable: no setting
 * lets an agent overwrite an approved translation.
 */
@Page("/app/settings/agents/new")
@Layout(".components.layouts.AppLayout")
@Composable
fun NewAgentPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val workspaceContext = WorkspaceRequestContext.current()
    val agentClaim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.AGENTS)
    val pageState = rememberNewAgentPageState(workspaceContext)

    with(pageState) {
        val managingContext = workspaceContext?.takeIf { it.canManageAgents }
        // Refuse before starting form-only work. A viewer must not learn billing or
        // project-directory state just by opening a URL they cannot act on.
        managingContext
            ?.takeIf { agentClaim?.remainsAuthorized(WorkspaceRequestContext.current()) == true }
            ?.let { ProjectDirectory.ensure(scope, it) }
        val billingState = if (managingContext != null) {
            loadWorkspaceProtectedResource(
                context = managingContext,
                privilege = WorkspacePrivilege.AGENTS,
                transport = ApiClient::workspaceBilling,
            )
        } else {
            Async.Success(null)
        }
        val requestedProjectSlugs = projects
            .split(",")
            .map(String::trim)
            .filter(String::isNotEmpty)
        val directoryVerified = workspaceContext
            ?.let { ProjectDirectory.canVerifyAllowlist(it) } == true
        val allowedProjectSlugs = workspaceContext
            ?.agentProjectAllowlist(ProjectDirectory.projects)
            .orEmpty()
            .map(ProjectSummaryDto::slug)
            .toSet()
        val unknownProjectSlugs = if (directoryVerified) {
            requestedProjectSlugs.filterNot(allowedProjectSlugs::contains)
        } else {
            emptyList()
        }
        val typedAllowlistBlocked = requestedProjectSlugs.isNotEmpty() &&
            (!directoryVerified || unknownProjectSlugs.isNotEmpty())

        // Navigates straight through once the token is safely on the clipboard; asks first if not.
        fun leaveFor(path: String) {
            if (copied) ctx.router.navigateTo(path) else pendingNav = path
        }

        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            PageHeader(
                t("app.settings.agents.new.connect_an_agent", "Connect an agent"),
                t("app.settings.agents.new.a_credential_for_a_bot_a", "A credential for a bot, a CI job or an MCP client — so nothing has to borrow a password."),
            )

            if (agentClaim == null) {
                LAlert(
                    t("app.settings.agents.new.this_is_not_yours_to_change", "Agent connections are managed by an owner or admin"),
                    t("app.settings.agents.new.an_agent_connection_can_spend_money_2", "An agent connection can spend money and write translations, so only owners and " +
                        "admins can create one."),
                    variant = LAlertVariant.INFO,
                )
                return@Column
            }

            created?.let { agent ->
                AgentSecretReveal(
                    agent = agent,
                    pageClaim = agentClaim,
                    copied = copied,
                    message = message,
                    pendingNav = pendingNav,
                    onCopiedChange = { copied = it },
                    onMessageChange = { message = it },
                    onPendingNavChange = { pendingNav = it },
                    onNavigateRequested = { leaveFor(it) },
                    navigate = ctx.router::navigateTo,
                )
                return@Column
            }

            AgentConnectionForm(
                state = pageState,
                pageClaim = agentClaim,
                scope = scope,
                billingState = billingState,
                requestedProjectSlugs = requestedProjectSlugs,
                directoryVerified = directoryVerified,
                unknownProjectSlugs = unknownProjectSlugs,
                typedAllowlistBlocked = typedAllowlistBlocked,
                onCancel = { ctx.router.navigateTo("/app/settings/agents") },
            )
        }
    }
}

@Composable
internal fun AgentSecretReveal(
    agent: AgentConnectionDto,
    pageClaim: WorkspaceActionClaim,
    copied: Boolean,
    message: String?,
    pendingNav: String?,
    onCopiedChange: (Boolean) -> Unit,
    onMessageChange: (String?) -> Unit,
    onPendingNavChange: (String?) -> Unit,
    onNavigateRequested: (String) -> Unit,
    navigate: (String) -> Unit,
) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LAlert(
                t("app.settings.agents.new.copy_this_now", "Copy this now"),
                t("app.settings.agents.new.it_is_shown_once_lingvo_stores_2", "It is shown once. Lingvo stores only a hash, so nobody — including support — " +
                    "can read it back to you."),
                variant = LAlertVariant.WARNING,
            )
            // The copy control belongs to the component rather than to whoever remembers to add
            // one: without it the only path that worked was triple-clicking a 40-character mono
            // string, and both other buttons navigate and unmount this card.
            LOneTimeSecret(
                value = agent.token.orEmpty(),
                label = tr("app.settings.agents.new.agent_token", "Agent token"),
                copyLabel = if (copied) {
                    t("app.settings.agents.new.copied", "Copied")
                } else {
                    t("app.settings.agents.new.copy_token", "Copy token")
                },
                onCopied = {
                    onCopiedChange(true)
                    onMessageChange(null)
                },
                onCopyFailed = { onMessageChange(clipboardRefusedNotice()) },
                trailingAction = {
                    LButton(
                        t("app.settings.agents.new.open_the_connection", "Open the connection"),
                        { onNavigateRequested("/app/settings/agents/${agent.id}") },
                        variant = LButtonVariant.SECONDARY,
                    )
                    LButton(
                        t("app.settings.agents.new.set_up_a_client", "Set up a client"),
                        { onNavigateRequested("/app/settings/agents/setup/claude") },
                        variant = LButtonVariant.SECONDARY,
                    )
                },
            )

            // Reached only by pressing copy and having it refused, so it is announced.
            message?.let {
                LAlert(
                    t("app.settings.agents.new.copy_it_by_hand", "Copy it by hand"),
                    it,
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }
        }
    }

    LModal(
        pendingNav != null,
        t("app.settings.agents.new.you_havent_copied_the_token", "You haven't copied the token"),
        { onPendingNavChange(null) },
        actions = {
            pendingNav?.let { target ->
                AgentCopyAndContinueAction(
                    pageClaim = pageClaim,
                    token = agent.token.orEmpty(),
                    target = target,
                    navigate = navigate,
                    onCopied = {
                        onPendingNavChange(null)
                        onCopiedChange(true)
                    },
                    onFailure = {
                        onPendingNavChange(null)
                        onMessageChange(clipboardRefusedNotice())
                    },
                )
            }
            LButton(
                t("app.settings.agents.new.leave_without_it", "Leave without it"),
                {
                    val target = pendingNav
                    onPendingNavChange(null)
                    target?.let { navigate(it) }
                },
                variant = LButtonVariant.GHOST,
            )
        },
    ) {
        LAlert(
            t("app.settings.agents.new.it_cannot_be_shown_again", "It cannot be shown again"),
            t("app.settings.agents.new.lingvo_stores_only_a_hash_leaving_2", "Lingvo stores only a hash. Leaving this page without the token means rotating " +
                "the connection and updating wherever you were about to paste it."),
            variant = LAlertVariant.WARNING,
        )
    }
}

/**
 * What to do when the clipboard refuses, on the one page where the token is never shown again.
 *
 * Written twice, in two whitespace arrangements of the same sentence, and neither reached the
 * catalogue: the extractor only follows a single-literal lookup, so a run joined by `+` is invisible
 * to it and a naive extraction of the two would have minted two keys for one meaning. One function,
 * one key, both call sites. `tr`, because both are event handlers.
 */
internal fun clipboardRefusedNotice(): String = tr(
    "app.settings.agents.new.couldnt_reach_the_clipboard_select_the",
    "Couldn't reach the clipboard. Select the token above and copy it manually before leaving.",
)
